<script lang="ts" setup>
import InputDecorator from './InputDecorator.vue';
import type {InputDecoratorProps} from './InputDecorator.vue';
import {computed} from 'vue';
import {
    DateDisplayMode,
    DateFieldConfig,
    FieldConfig,
    FieldType,
    FieldValue,
    FormFieldValue,
    OptinFieldConfig
} from '@Common/Components/Form/types';
import useCurrentLocale from "@Common/Composables/current-locale";
import {useI18n} from "vue-i18n";
import Link from "@Components/Buttons/Link.vue";

const props = withDefaults(defineProps<{
    id: string;
    name: string;
    value?: FieldValue | FormFieldValue;
    fieldType?: string;
    field?: FieldConfig|undefined;
} & InputDecoratorProps>(), {
    id: '',
    name: '',
    value: '',
    fieldType: FieldType.Text,
});

const {t} = useI18n();
const {isoLocale} = useCurrentLocale();

const labelList = computed(() => {
    if (props.fieldType === FieldType.OptIn) {
        return showSignature();
    }

    if (props.value && Array.isArray(props.value)) {
        return props.value.map(v => (typeof v === 'object' && v?.label) ? v.label : null).filter(label => label);
    }

    return [];
});

const htmlValue = computed(() => {
    if (props.value === '' || Array.isArray(props.value)) {
        return '';
    }

    if (props.fieldType === FieldType.Date) {
        return convertDate();
    }

    if (typeof props.value === 'object' && props.value && 'label' in props.value) {
        return props.value?.label || '';
    }

    if (typeof props.value === 'boolean') {
        return props.value ? t('yes') : t('no');
    }

    return props.value;
});

const canPreviewFile = computed(() => {
    if (props.fieldType === FieldType.File || props.fieldType === FieldType.Upload) {
        const imgExt = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.bmp', '.webp'];
        const url = props.value as string;
        const fileExtension = url ? url.substring(url.lastIndexOf('.')) : '';
        return imgExt.includes(fileExtension.toLowerCase());
    }
    return false;
});

const convertDate = () => {
    const date = new Date(props.value as string);

    if (isNaN(date.getTime())) {
        return '';
    }

    if(props.field === undefined){
        return date.toLocaleDateString(isoLocale.value, {day: 'numeric', month: 'long', year: 'numeric'});
    }

    const field = props.field as DateFieldConfig;

    const formattedDate = date.toLocaleDateString(isoLocale.value, {year: 'numeric', month: 'short', day: '2-digit'});

    if (field.displayMode === DateDisplayMode.Age || field.displayMode === DateDisplayMode.DateAndAge) {

        const age = new Date().getFullYear() - date.getFullYear();

        const formattedAge = `${age} ${t('form.date.years_old')}`;

        if (field.displayMode === DateDisplayMode.Age) {
            return formattedAge;
        }

        return `${formattedDate} (${formattedAge})`;
    }

    return formattedDate;
}

const showSignature = () => {
    const field = props.field as OptinFieldConfig;
    const {signature} = field as OptinFieldConfig;
    if (signature) {
        const createdAt = new Date(signature.createdAt).toLocaleDateString(isoLocale.value, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        });
        return [
            t('field.optin.date', {createdAt: createdAt}),
            t('field.optin.ip', signature)
        ];
    }
    return [];
}
</script>
<template>
    <InputDecorator
        :for-id="id"
        :label="label"
        :help="help"
        :help-position="helpPosition"
    >
        <p v-if="fieldType === FieldType.File || fieldType === FieldType.Upload" class="form-control-static">
            <img v-if="canPreviewFile" :src="htmlValue as string" :alt="htmlValue as string"/>
            <Link v-else :href="htmlValue as string">{{ htmlValue }}</Link>
        </p>
        <div v-else-if="labelList.length > 0" class="form-control-static">
            <ul class="list-unstyled">
                <li v-for="(label, i) in labelList" :key="i">{{ label }}</li>
            </ul>
        </div>
        <div v-else class="form-control-static" v-html="htmlValue"></div>
    </InputDecorator>
</template>